import {Box, Button, Modal, Typography} from '@mui/material';
import React, {useCallback, useState} from 'react';
import styles from '../../styles';
import {Control, FieldValues, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useAppDispatch} from '../../store/hooks';
import FormTextField from '../shared/FormTextField';
import {createDrillAsync, getDrillsAsync} from '../../store/drillSlice';

type DrillFormValues = {
  name: string;
  description: string;
};

interface Props {
  teamId: number;
}

const CreateDrillDialog: React.FC<Props> = ({teamId}) => {
  const {t} = useTranslation();
  const dispatch = useAppDispatch();
  const {control, handleSubmit, reset} = useForm<DrillFormValues>();
  const [showAddDrillDialog, setShowAddDrillDialog] = useState(false);
  const [pending, setPending] = useState(false);

  const handleAddDrillClose = useCallback(() => {
    reset();
    setShowAddDrillDialog(false);
  }, [reset]);

  const handleAddSubmit = useCallback(
    async (data: DrillFormValues) => {
      if (!teamId) {
        console.error('Team ID is required');
        return;
      }
      setPending(true);
      await dispatch(
        createDrillAsync({
          name: data.name,
          description: data.description,
          teamId: Number(teamId),
        }),
      );
      dispatch(getDrillsAsync({teamId: Number(teamId)}));
      setPending(false);
      handleAddDrillClose();
    },
    [teamId, dispatch, handleAddDrillClose],
  );

  return (
    <>
      <Button
        sx={{mb: 2, mr: 2}}
        disabled={pending}
        onClick={() => setShowAddDrillDialog(true)}>
        {t('library.addDrill')}
      </Button>
      <Modal
        open={showAddDrillDialog}
        onClose={handleAddDrillClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box sx={styles.modal}>
          <Typography variant="h4" sx={{mb: 2}}>
            {t('library.addDrill')}
          </Typography>
          <form onSubmit={handleSubmit(handleAddSubmit)} className="form">
            <FormTextField
              control={control as unknown as Control<FieldValues>}
              name="name"
              label={t('general.name') as string}
              required
            />
            <FormTextField
              control={control as unknown as Control<FieldValues>}
              name="description"
              label={t('general.description') as string}
            />
            <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
              <Button variant="outlined" onClick={handleAddDrillClose}>
                {t('general.cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={pending}
                sx={{ml: 2}}>
                {t('general.save')}
              </Button>
            </Box>
          </form>
        </Box>
      </Modal>
    </>
  );
};

export default CreateDrillDialog;
